import React, { useState } from 'react';

import { Link, NavLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AccountCircleRounded, CloseRounded, ExpandMoreRounded, MenuRounded } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';

import CartButton from './CartButton.component';
import { useFindPublicCategories } from '../../../categories/hooks/public/find-categories.hook';
import { MAIN_MENU_ENTRIES } from '../constants/main-menu.constants';
import { MainMenuEntry } from '../types/main-menu-entry.type';
import { MainMenuLink } from '../types/main-menu-link.type';
import { PUBLIC_PRODUCTS_APP_PATHS } from '../../../products/constants/public-products-paths.constants';
import { resolveHomeLink } from '../utils/resolve-home-link.util';
import { usePanel } from '../hooks/use-panel.hook';
import { PUBLIC_PANELS } from '../constants/panel.constants';

const SKELETON_COUNT = 5;

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
    boxShadow: 'none',
    color: '#1c1c1c',
  },
  toolbarRow: {
    padding: '8px 16px',
  },
  logo: {
    display: 'block',
    textAlign: 'center',
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#1c1c1c',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  iconButton: {
    minWidth: 44,
    minHeight: 44,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  drawerPaper: {
    width: 280,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  drawerTitle: {
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
  },
  sectionTitle: {
    padding: '12px 16px 4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  drawerLink: {
    width: '100%',
    padding: '12px 16px',
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    color: '#1c1c1c',
    textDecoration: 'none',
    fontSize: '0.95rem',

    '&:hover': {
      color: theme.palette.client.primary.main,
    },
  },
  drawerLinkActive: {
    color: theme.palette.client.primary.main,
    fontWeight: 600,
  },
  drawerLoginLink: {
    color: theme.palette.client.primary.main,
  },
  drawerListItem: {
    padding: 0,
  },
  // L'accordéon reprend l'aspect d'une simple ligne de menu : ni ombre, ni marge quand il s'ouvre.
  accordion: {
    boxShadow: 'none',

    '&:before': {
      display: 'none',
    },

    '&.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    padding: '0 16px',
    minHeight: 44,

    '&.Mui-expanded': {
      minHeight: 44,
    },
  },
  accordionSummaryContent: {
    margin: 0,
    fontSize: '0.95rem',

    '&.Mui-expanded': {
      margin: 0,
    },
  },
  accordionDetails: {
    padding: 0,
    flexDirection: 'column',
  },
  childLink: {
    width: '100%',
    padding: '10px 16px 10px 32px',
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
}));

const MobileAppBar: React.FC = () => {
  const classes = useStyles();
  const { loading, categories } = useFindPublicCategories();
  const { openPanel } = usePanel();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const homeLink = resolveHomeLink(loading, categories);
  const closeDrawer = () => setIsDrawerOpen(false);

  const openLoginPanel = () => {
    closeDrawer();
    openPanel(PUBLIC_PANELS.LOGIN);
  };

  const renderChild = (child: MainMenuLink) => {
    if (!child.path) {
      return (
        <span key={child.label} className={classes.childLink}>
          {child.label}
        </span>
      );
    }

    return (
      <Link key={child.label} to={child.path} className={classes.childLink} onClick={closeDrawer}>
        {child.label}
      </Link>
    );
  };

  const renderEntry = (entry: MainMenuEntry) => {
    if (entry.children) {
      return (
        // `unmountOnExit` garde le drawer léger et rend l'état replié réellement vide.
        <Accordion key={entry.label} square elevation={0} className={classes.accordion} TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            classes={{ root: classes.accordionSummary, content: classes.accordionSummaryContent }}
          >
            {entry.label}
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>{entry.children.map(renderChild)}</AccordionDetails>
        </Accordion>
      );
    }

    if (!entry.path) {
      return (
        <span key={entry.label} className={classes.drawerLink}>
          {entry.label}
        </span>
      );
    }

    return (
      <Link key={entry.label} to={entry.path} className={classes.drawerLink} onClick={closeDrawer}>
        {entry.label}
      </Link>
    );
  };

  return (
    <AppBar position="sticky" className={classes.appBar}>
      {/* Le logo occupe l'espace restant entre les deux boutons, qui se dimensionnent sur leur icône. */}
      <Grid container alignItems="center" className={classes.toolbarRow}>
        <Grid item xs="auto">
          <IconButton className={classes.iconButton} aria-label="Ouvrir le menu" onClick={() => setIsDrawerOpen(true)}>
            <MenuRounded />
          </IconButton>
        </Grid>

        <Grid item xs>
          <Typography component={Link} to={homeLink} className={classes.logo}>
            Panier Bio Paris
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <div className={classes.actions}>
            <IconButton className={classes.iconButton} aria-label="Se connecter" onClick={openLoginPanel}>
              <AccountCircleRounded />
            </IconButton>
            <CartButton />
          </div>
        </Grid>
      </Grid>

      <Drawer anchor="left" open={isDrawerOpen} onClose={closeDrawer} classes={{ paper: classes.drawerPaper }}>
        <div className={classes.drawerHeader}>
          <Typography className={classes.drawerTitle}>Menu</Typography>
          <IconButton className={classes.iconButton} aria-label="Fermer le menu" onClick={closeDrawer}>
            <CloseRounded />
          </IconButton>
        </div>
        <Divider />

        {MAIN_MENU_ENTRIES.map(renderEntry)}

        <Divider />
        <Typography className={classes.sectionTitle}>Catégories</Typography>
        <List disablePadding>
          {loading &&
            Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={index}>
                <Skeleton variant="text" width="100%" height={24} />
              </ListItem>
            ))}
          {!loading &&
            categories.map((category) => (
              <ListItem key={category.id} className={classes.drawerListItem}>
                <NavLink
                  to={PUBLIC_PRODUCTS_APP_PATHS.CATALOG_FOR(category.slug)}
                  className={classes.drawerLink}
                  activeClassName={classes.drawerLinkActive}
                  onClick={closeDrawer}
                >
                  {category.name}
                </NavLink>
              </ListItem>
            ))}
        </List>

        <Divider />
        <MuiLink
          component="button"
          type="button"
          className={`${classes.drawerLink} ${classes.drawerLoginLink}`}
          onClick={openLoginPanel}
        >
          Se connecter
        </MuiLink>
      </Drawer>
    </AppBar>
  );
};

export default MobileAppBar;
